import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { switchMembership } from '../store/slices/authSlice';
import { useSwitchContextMutation } from '../store/api/localApi';

/**
 * Sync the active PARENT membership (and X-Active-Context header) with the
 * location of a child the parent is switching to. Without this, a parent with
 * kids at two locations picks a Blueball kid while their active context is
 * still Scranton — /forms, /classes and similar queries scoped by the header
 * then return the wrong (or forbidden) data (see Bug 9).
 *
 * No-op when the active membership is already PARENT at the child's location
 * (covers the same-location, two-kids case) or when the parent has no PARENT
 * membership at that location (leave the request to fail naturally rather
 * than pretend permission we don't have).
 */
export function useSyncParentContext() {
  const dispatch = useAppDispatch();
  const [switchContext] = useSwitchContextMutation();
  const user = useAppSelector((s) => s.auth.user);
  const activeIndex = useAppSelector((s) => s.auth.activeMembershipIndex);

  return useCallback(
    async (childLocationId: string, childOrganizationId?: string) => {
      const memberships = user?.memberships;
      if (!memberships?.length || !childLocationId) return;
      const active = memberships[activeIndex];
      if (active?.role === 'PARENT' && active.locationId === childLocationId) return;
      const targetIdx = memberships.findIndex(
        (m) => m.role === 'PARENT' && m.locationId === childLocationId
      );
      if (targetIdx < 0) return;
      const target = memberships[targetIdx];
      const prev = activeIndex;
      dispatch(switchMembership(targetIdx));
      try {
        await switchContext({
          role: 'PARENT',
          locationId: target.locationId || undefined,
          organizationId: target.organizationId || childOrganizationId || undefined,
        }).unwrap();
      } catch {
        dispatch(switchMembership(prev));
      }
    },
    [user, activeIndex, dispatch, switchContext]
  );
}
